import { useContext } from 'react'
import { useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import { faBan, faCheckCircle } from '@fortawesome/pro-light-svg-icons'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'

import {
  Box, Button, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import type { TagsFilters } from '@hooks/useTags'
import useCurrentUser from '@hooks/useCurrentUser'
import useTags from '@hooks/useTags'
import { generateUrls } from '@hooks/useTag'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

import TagListItem from '../TagListItem'

const buildPageFilterFields = (currentUserPayload: ReturnType<typeof useCurrentUser>) => {
  const { isBrandUser } = currentUserPayload

  const boxProps = { marginBottom: 'large' }
  const style = { marginBottom: 16 }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title or ID',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All',
        inputComponent: 'select',
        style,
      },
      key: 'global',
      label: 'Global',
      values: [
        { key: 'true', label: 'True' },
        { key: 'false', label: 'False' },
      ],
    },
    {
      callbacks: {
        shouldRender: ({ global }: { global?: string }) => global !== 'true',
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'brands',
      },
      hidden: isBrandUser,
      key: 'owner_id',
    },
  ]
}

interface PageContentProps {
  pageFilters: TagsFilters,
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props
  const { owner_id } = pageFilters

  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  const { hidden }: { hidden?: string } = parsePermittedQueryParams(getQueryParamsFromHash(), ['hidden'])
  const isHidden = hidden || 'false'

  const { tagsIndexUrl, hiddenTagsIndexUrl } = generateUrls()

  const currentUserPayload = useCurrentUser()
  const { currentUser, isBrandUser } = currentUserPayload

  const tagsPayload = useTags({
    filters: {
      hidden: isHidden,
      ...pageFilters,
      owner_type: owner_id ? 'Brand' : undefined,
    },
    performHttpRequests: true,
    requestOptions: defaultRequestOptions.tag,
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredTags,
    filteredTagsCount,
    hasFilteredTags,
    loading,
  } = tagsPayload

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      showCreateOrEditTagModal,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  return (
    <>
      <Helmet>
        <title>Tags | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button
            buttonStyle="primaryCreate"
            onClick={() => showCreateOrEditTagModal({
              tag: {
                global: !isBrandUser,
                owner_id: isBrandUser ? currentUser.brand_id : undefined,
                owner_type: isBrandUser ? 'Brand' : '',
              },
            })}
            size="medium"
          >
            Create
          </Button>
        )}
        activeTabBarItemKey={isHidden}
        tabBarItems={[
          {
            href: tagsIndexUrl,
            icon: faCheckCircle,
            key: 'false',
            title: 'Active',
          },
          {
            href: hiddenTagsIndexUrl,
            icon: faBan,
            key: 'true',
            title: 'Inactive',
          },
        ]}
        title="Tags"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredTags && <ListBlankState />}

            {!loading && hasFilteredTags && (
              <DashboardModule title="Tags">
                <Box flexDirection="column">
                  <Box flexDirection="column">
                    {filteredTags.map(tag => (
                      <TagListItem key={tag.id} tag={tag} />
                    ))}
                  </Box>

                  <DashboardModule.LoadMoreFooter
                    callbacks={{ loadMore }}
                    canLoadMore={canLoadMore}
                    entityCount={filteredTagsCount}
                    loading={loading}
                  />
                </Box>
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields(currentUserPayload)}
              pageFilters={pageFilters}
              title="Filters"
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
